/**
 * Driver roster management — Settings dialog tab.
 *
 * Simpler than Carrier Users: drivers are text-only contacts (no login, no
 * approval workflow, no sign-in history) — just a per-carrier roster with
 * add / edit / disable / restore. Requires EntraGroupSchedulers, same bar as
 * Carrier Users, not full Admin — see admin.ts's assertScheduler.
 *
 * Pick a carrier first (drivers are scoped per-carrier), then manage that
 * carrier's roster. Disable is soft — a disabled driver can be restored, and
 * historical appointments keep their driver-name/phone snapshot regardless
 * (see driverRepository.ts header for why there's no hard-delete).
 */

import { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, RotateCcw, Ban, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
    ApiCallError,
    adminListCarriers,
    adminListCarrierDrivers,
    adminCreateCarrierDriver,
    adminUpdateCarrierDriver,
    adminDisableCarrierDriver,
    adminRestoreCarrierDriver,
    CarrierAdminRow,
    CarrierDriverAdminRow,
} from '@/api/client';
import { ConfirmDialog } from '@/components/ConfirmDialog';

interface PendingConfirm {
    title: string;
    message: string;
    confirmLabel: string;
    destructive?: boolean;
    onConfirm: () => void;
}

interface DriversSectionProps {
    getToken: () => Promise<string>;
    onChanged: () => void;
}

export function DriversSection({ getToken, onChanged }: DriversSectionProps) {
    const [carriers, setCarriers] = useState<CarrierAdminRow[] | null>(null);
    const [carrierCode, setCarrierCode] = useState<string | null>(null);
    const [drivers, setDrivers] = useState<CarrierDriverAdminRow[] | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);

    const loadCarriers = useCallback(async () => {
        try {
            const token = await getToken();
            const rows = await adminListCarriers(token);
            setCarriers(rows.filter(c => c.IsActive));
        } catch (e) {
            setLoadError(e instanceof ApiCallError ? e.message : 'Failed to load carriers.');
        }
    }, [getToken]);

    const loadDrivers = useCallback(async (code: string) => {
        setDrivers(null);
        try {
            const token = await getToken();
            const rows = await adminListCarrierDrivers(token, code);
            setDrivers(rows);
        } catch (e) {
            setLoadError(e instanceof ApiCallError ? e.message : 'Failed to load drivers.');
        }
    }, [getToken]);

    useEffect(() => { loadCarriers(); }, [loadCarriers]);
    useEffect(() => { if (carrierCode) loadDrivers(carrierCode); }, [carrierCode, loadDrivers]);

    const refresh = async () => {
        if (carrierCode) await loadDrivers(carrierCode);
        onChanged();
    };

    return (
        <div>
            {loadError && (
                <div className="text-sm rounded-md p-3 mb-3 bg-pipeline-rose-bg text-pipeline-rose-text border border-pipeline-rose-border flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>{loadError}</span>
                </div>
            )}

            <div className="mb-4">
                <Label htmlFor="driver-carrier-picker">Carrier</Label>
                <Select value={carrierCode ?? ''} onValueChange={setCarrierCode}>
                    <SelectTrigger id="driver-carrier-picker" className="mt-1.5 max-w-sm">
                        <SelectValue placeholder={carriers === null ? 'Loading carriers…' : 'Select a carrier'} />
                    </SelectTrigger>
                    <SelectContent>
                        {(carriers ?? []).map((c) => (
                            <SelectItem key={c.CarrierCode} value={c.CarrierCode}>
                                {c.CarrierName} ({c.CarrierCode})
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {carrierCode && (
                <DriverRoster
                    carrierCode={carrierCode}
                    drivers={drivers}
                    getToken={getToken}
                    onChanged={refresh}
                />
            )}
        </div>
    );
}

function DriverRoster({
    carrierCode,
    drivers,
    getToken,
    onChanged,
}: {
    carrierCode: string;
    drivers: CarrierDriverAdminRow[] | null;
    getToken: () => Promise<string>;
    onChanged: () => Promise<void>;
}) {
    const [addOpen, setAddOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [busyId, setBusyId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null);

    const active = (drivers ?? []).filter(d => d.isActive);
    const inactive = (drivers ?? []).filter(d => !d.isActive);

    const runDisable = async (d: CarrierDriverAdminRow) => {
        setBusyId(d.driverId);
        setError(null);
        try {
            const token = await getToken();
            await adminDisableCarrierDriver(token, d.driverId);
            await onChanged();
        } catch (e) {
            setError(e instanceof ApiCallError ? e.message : 'Disable failed.');
        } finally {
            setBusyId(null);
        }
    };
    const confirmDisable = (d: CarrierDriverAdminRow) => setPendingConfirm({
        title: 'Disable driver?',
        message: `Disable ${d.name}? They'll no longer be selectable for new appointments, but past appointments keep their record.`,
        confirmLabel: 'Disable',
        destructive: true,
        onConfirm: () => runDisable(d),
    });

    const runRestore = async (d: CarrierDriverAdminRow) => {
        setBusyId(d.driverId);
        setError(null);
        try {
            const token = await getToken();
            await adminRestoreCarrierDriver(token, d.driverId);
            await onChanged();
        } catch (e) {
            setError(e instanceof ApiCallError ? e.message : 'Restore failed.');
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div>
            {pendingConfirm && (
                <ConfirmDialog
                    open={true}
                    title={pendingConfirm.title}
                    message={pendingConfirm.message}
                    confirmLabel={pendingConfirm.confirmLabel}
                    destructive={pendingConfirm.destructive}
                    onConfirm={() => { const c = pendingConfirm; setPendingConfirm(null); c.onConfirm(); }}
                    onCancel={() => setPendingConfirm(null)}
                />
            )}
            {error && (
                <div className="text-sm rounded-md p-3 mb-3 bg-pipeline-rose-bg text-pipeline-rose-text border border-pipeline-rose-border flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                </div>
            )}

            <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-sm flex items-center gap-2">
                    Drivers
                    {drivers && <span className="text-xs text-muted-foreground font-normal">({active.length})</span>}
                </h3>
                {!addOpen && (
                    <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}>
                        <Plus className="h-3.5 w-3.5" /> Add driver
                    </Button>
                )}
            </div>

            {addOpen && (
                <DriverForm
                    carrierCode={carrierCode}
                    getToken={getToken}
                    onCancel={() => setAddOpen(false)}
                    onSaved={async () => { setAddOpen(false); await onChanged(); }}
                />
            )}

            {drivers === null ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading...
                </div>
            ) : active.length === 0 && !addOpen ? (
                <div className="text-sm text-muted-foreground py-3 px-3 border rounded-md">
                    No drivers on file for this carrier yet.
                </div>
            ) : (
                <div className="border rounded-md divide-y">
                    {active.map((d) => (
                        editingId === d.driverId ? (
                            <div key={d.driverId} className="px-3 py-2.5">
                                <DriverForm
                                    carrierCode={carrierCode}
                                    getToken={getToken}
                                    existing={d}
                                    onCancel={() => setEditingId(null)}
                                    onSaved={async () => { setEditingId(null); await onChanged(); }}
                                />
                            </div>
                        ) : (
                            <div key={d.driverId} className="flex items-center justify-between gap-3 px-3 py-2.5">
                                <div className="min-w-0">
                                    <div className="text-sm font-medium truncate">{d.name}</div>
                                    <div className="text-xs text-muted-foreground truncate">
                                        {d.phone}{d.email ? ` · ${d.email}` : ''}
                                    </div>
                                </div>
                                <div className="flex gap-2 shrink-0">
                                    <Button size="sm" variant="ghost" onClick={() => setEditingId(d.driverId)}>
                                        Edit
                                    </Button>
                                    <Button
                                        onClick={() => confirmDisable(d)}
                                        disabled={busyId === d.driverId}
                                        size="sm"
                                        variant="outline"
                                    >
                                        {busyId === d.driverId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ban className="h-3.5 w-3.5" />}
                                        Disable
                                    </Button>
                                </div>
                            </div>
                        )
                    ))}
                </div>
            )}

            {inactive.length > 0 && (
                <details className="mt-4">
                    <summary className="text-xs text-muted-foreground cursor-pointer select-none">
                        Disabled drivers ({inactive.length})
                    </summary>
                    <div className="border rounded-md divide-y mt-2">
                        {inactive.map((d) => (
                            <div key={d.driverId} className="flex items-center justify-between gap-3 px-3 py-2.5 opacity-70">
                                <div className="min-w-0">
                                    <div className="text-sm font-medium truncate">{d.name}</div>
                                    <div className="text-xs text-muted-foreground truncate">
                                        {d.phone}{d.email ? ` · ${d.email}` : ''}
                                    </div>
                                </div>
                                <Button
                                    onClick={() => runRestore(d)}
                                    disabled={busyId === d.driverId}
                                    size="sm"
                                    variant="outline"
                                >
                                    {busyId === d.driverId ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                                    Restore
                                </Button>
                            </div>
                        ))}
                    </div>
                </details>
            )}
        </div>
    );
}

function DriverForm({
    carrierCode,
    existing,
    getToken,
    onCancel,
    onSaved,
}: {
    carrierCode: string;
    existing?: CarrierDriverAdminRow;
    getToken: () => Promise<string>;
    onCancel: () => void;
    onSaved: () => Promise<void>;
}) {
    const [name, setName] = useState(existing?.name ?? '');
    const [phone, setPhone] = useState(existing?.phone ?? '');
    const [email, setEmail] = useState(existing?.email ?? '');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const submit = async () => {
        if (!name.trim() || !phone.trim()) {
            setError('Name and phone are both required.');
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const token = await getToken();
            if (existing) {
                await adminUpdateCarrierDriver(token, existing.driverId, {
                    name: name.trim(), phone: phone.trim(), email: email.trim() || null,
                });
            } else {
                await adminCreateCarrierDriver(token, {
                    carrierCode, name: name.trim(), phone: phone.trim(), email: email.trim() || null,
                });
            }
            await onSaved();
        } catch (e) {
            setError(e instanceof ApiCallError ? e.message : 'Save failed.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="border rounded-md p-3 mb-3 space-y-2 bg-muted/30">
            <div className="grid grid-cols-2 gap-2">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Driver name" disabled={busy} />
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 555 5555" disabled={busy} />
            </div>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (optional)" disabled={busy} />
            {error && <div className="text-xs text-destructive">{error}</div>}
            <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Button>
                <Button size="sm" onClick={submit} disabled={busy}>
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                    {existing ? 'Save' : 'Add driver'}
                </Button>
            </div>
        </div>
    );
}
